import { ClickAwayListener } from '@mui/material';
import Button from '@platform-ui-common/components/Button/index.js';
import { IconType } from '@platform-ui-common/components/Icon/Icon.types.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import ProgressPopper from '@platform-ui-common/components/ProgressPopper/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import useProgress from '@platform-ui-common/hooks/useProgress/useProgress.js';
import Color from '@platform-ui-common/utilities/Color.js';
import { formatPercentage } from '@platform-ui-common/utilities/format.js';
import LogOutPopper from '@platform-ui/auth/components/LogOutPopper/index.js';
import MyProfilePopper from '@platform-ui/auth/components/MyProfilePopper/index.js';
import cx from 'classnames';
import { ReactNode, useRef, useState } from 'react';

type PopperComponent = ({
  onClickClose,
  isOpen,
  anchorEl,
  ...rest
}: {
  onClickClose: () => void;
  isOpen: boolean;
  anchorEl?: HTMLButtonElement;
}) => JSX.Element;

function ToolbarItem({
  children,
  icon,
  className,
  onClick,
  Popper,
}: {
  children: ReactNode;
  icon: IconType | JSX.Element;
  className?: string;
  onClick?: () => void;
  Popper?: PopperComponent;
}): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>();
  const [popperOpen, setPopperOpen] = useState(false);
  const clickAwayHandler = (): void => setPopperOpen(false);

  const mobileStyles = 'flex flex-col flex-auto items-center justify-center h-full gap-y-1';
  const desktopStyles = cx(
    'lg:w-20 lg:h-20 lg:rounded-full lg:shadow-toolbar',
    popperOpen ? 'lg:bg-primary lg:text-white' : 'lg:bg-white lg:text-primary',
  );
  const { ...rest } = Popper;
  return (
    <ClickAwayListener onClickAway={clickAwayHandler}>
      <div className={className}>
        <Button
          type="button"
          variant="text"
          textColor={Color.Primary}
          onClick={(): void => {
            setPopperOpen((val) => !val);
            if (onClick) onClick();
          }}
          ref={buttonRef}
        >
          <span className={`${mobileStyles} ${desktopStyles} normal-case`}>
            {typeof icon === 'string' ? <Icon type={icon} size="LG" /> : icon}
            <Typography tag="span" variant="fieldLabel">
              {children}
            </Typography>
          </span>
        </Button>
        {Popper && (
          <Popper
            isOpen={popperOpen}
            onClickClose={(): void => setPopperOpen(false)}
            anchorEl={buttonRef.current}
            {...rest}
          />
        )}
      </div>
    </ClickAwayListener>
  );
}

export default function UserToolbar(): JSX.Element {
  const { progress } = useProgress();

  const mobileStyles = `
    bg-white shadow-toolbar border-t border-t-grayBackground
    h-18 flex justify-evenly items-center sticky bottom-0`;
  const desktopStyles = `
    lg:bg-transparent lg:shadow-none lg:border-t-0 lg:h-auto
    lg:absolute lg:left-[5%] lg:top-10 lg:-translate-x-1/2 lg:flex-col lg:gap-y-5 lg:bottom-auto`;

  return (
    <div className={`${mobileStyles} ${desktopStyles}`}>
      <ToolbarItem icon="profile" Popper={MyProfilePopper}>
        My Profile
      </ToolbarItem>
      {progress && (
        <ToolbarItem
          className="lg:hidden"
          icon={<span className="leading-6">{formatPercentage(progress?.percentage)}</span>}
          Popper={ProgressPopper}
        >
          Progress
        </ToolbarItem>
      )}
      <ToolbarItem icon="logout" Popper={LogOutPopper}>
        Log Out
      </ToolbarItem>
    </div>
  );
}
